import _ from "lodash";
import checkAvsStatusError from "~/assets/utils/Avs/checkAvsStatusError";
import getAvsParams, { isRegionEntry } from "~/assets/utils/getAvsParams";
import { getStore } from "~/assets/utils/storeAccessor";
import updateDetails from "~/assets/utils/updateDetails";
import { AVSStatus, AVSSubStatus } from "~/entities/AVS";
import { Details } from "~/entities/Details";

// import checkIfDetailUpdated from '~/assets/utils/checkIfDetailUpdated';

export interface IChangedFlags {
  isAddress1Changed: boolean;
  isAddress2Changed: boolean;
  isCityChanged: boolean;
  isCountryChanged: boolean;
  isPostalChanged: boolean;
  isStateChanged: boolean;
}

export default async function (details: Details): Promise<{
  stageDetails: Details;
  updatedDetails: Details;
  changedFlags: IChangedFlags;
}> {
  // call avs endpoint to see if it can add to the details on every stage from an to
  // let needsAVSModal = false;
  let changedFlags: IChangedFlags = {
    isAddress1Changed: false,
    isAddress2Changed: false,
    isCityChanged: false,
    isCountryChanged: false,
    isPostalChanged: false,
    isStateChanged: false,
  };
  let stageDetails: Details = _.cloneDeep(details);
  let updatedDetails: Details = _.cloneDeep(details);
  let error: any;
  const store = getStore();
  const NeedsAVSCheck = !stageDetails?.passedAvs && !stageDetails?.avsChecked;
  // BIK-2095: single-input path. When a one-line address was pasted or typed into the main
  // input (flag on), verify it directly via the AVS `region` param (UPS XAV Region mode)
  // instead of the segmented fields. This keeps the entered value intact — no Google Places.
  const avsRawPasteDirect = (store as any)?.$config?.avsRawPasteDirect === true;
  const isRegionPaste = avsRawPasteDirect && isRegionEntry(stageDetails);
  if (!isRegionPaste) {
    if (
      !stageDetails.address1 ||
      !stageDetails.streetAddress ||
      !stageDetails.city ||
      !stageDetails.postalCode ||
      !stageDetails.country ||
      !stageDetails.state
    ) {
      return {
        stageDetails,
        updatedDetails,
        changedFlags,
      };
    }
  }
  if (NeedsAVSCheck) {
    const avsCheck = await store.dispatch(
      "ship/requestAVSCheck",
      getAvsParams(stageDetails)
    );
    // BIK-2095: for the region-paste path the detail starts with no segmented
    // state/country objects, so updateDetails would drop the parsed state/country from
    // ValidAddress. Seed empty objects so the UPS-parsed values land on the detail.
    if (isRegionPaste) {
      if (!stageDetails.state) {
        stageDetails.state = {
          country: null,
          id: "",
          stateprov: "",
          stateprovcode: "",
        } as Details["state"];
      }
      if (!stageDetails.country) {
        stageDetails.country = {
          active: true,
          code: "",
          id: "",
          name: "",
        } as Details["country"];
      }
      // A verified one-line paste is a complete address, same as a resolved Google
      // full-address selection. Set searchType so downstream validators (which assume
      // Places always set it) don't hit undefined.searchType.
      if (!stageDetails.searchType) {
        stageDetails.searchType = "full";
      }
    }
    // needs to add to the details the residential mark if the avs return true on that
    if (avsCheck && typeof avsCheck.passedAvs === "boolean") {
      if (avsCheck.isChanged) {
        const updatedDetailFrom = _.cloneDeep(stageDetails);
        updatedDetails = updateDetails(avsCheck, updatedDetailFrom);
        changedFlags = avsCheck.changedFlags;
        stageDetails.passedAvs = avsCheck.passedAvs;
        if (stageDetails.avsStatus) {
          stageDetails.avsStatus.status = avsCheck.passedAvs
            ? AVSStatus.success
            : AVSStatus.failed;
          if (!avsCheck.passedAvs) {
            stageDetails.avsStatus.subStatus = checkAvsStatusError(avsCheck);
            stageDetails.avsProblem = true;
            error = avsCheck.errorMessage;
          }
        }
      } else {
        stageDetails = updateDetails(avsCheck, stageDetails);
        updatedDetails = stageDetails;
        if (avsCheck.passedAvs) {
          stageDetails.avsStatus.status = AVSStatus.success;
        } else {
          stageDetails.avsStatus.status = AVSStatus.failed;
          stageDetails.avsStatus.subStatus = checkAvsStatusError(avsCheck);
          stageDetails.avsProblem = true;
          error = avsCheck.errorMessage;
        }
      }
    }
    // BIK-1995: the unit-required check is part of the ship step 1 AVS rework.
    // Skip the request entirely when the flag is off (prod default).
    const avsShip1Enabled = (store as any)?.$config?.avsShip1Rework === true;
    if (avsShip1Enabled && stageDetails.passedAvs) {
      const unitRequired = await store.dispatch(
        "ship/requestAVSUnitCheck",
        getAvsParams(stageDetails)
      );
      if (unitRequired === true) {
        stageDetails.avsStatus.status = AVSStatus.failed;
        stageDetails.avsStatus.subStatus = AVSSubStatus.unitNotFound;
        stageDetails.passedAvs = false;
        stageDetails.avsProblem = true;
      }
    }
  }
  // stageDetails: the details that are going to be used to check if the avs is needed
  // updatedDetails: the details updated with the avs result, if the AVS modal is needed
  // needsAVSModal: if the AVS modal is needed
  const avsObject: {
    stageDetails: Details;
    updatedDetails: Details;
    changedFlags: IChangedFlags;
    error?: any;
  } = {
    stageDetails,
    updatedDetails,
    changedFlags,
    error,
  };
  return avsObject;
}
